// Crew Settings Account Tab - company info, admin toggles, connected clinics
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Edit } from 'lucide-react';

import { ErrorToast } from 'components';
import ClientService from 'api/client/client';
import ClientDetailsForm from '../features/clientDetails/ClientDetails_Form';
import ExternalSyncCard from './external-sync/ExternalSyncCard';

// ---- sub-components ----

const InfoField = ({ label, value }) => (
  <div>
    <p className="tw-text-xs tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium tw-mb-1">
      {label}
    </p>
    <p className="tw-text-sm tw-text-gray-800 tw-font-medium tw-m-0">{value || '—'}</p>
  </div>
);

const ToggleSwitch = ({ checked, onChange, label, description }) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-py-3">
    <div>
      <p className="tw-text-sm tw-font-medium tw-text-gray-800 tw-m-0">{label}</p>
      {description && (
        <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5 tw-mb-0">{description}</p>
      )}
    </div>
    <button
      type="button"
      onClick={onChange}
      className={[
        'tw-relative tw-inline-flex tw-h-6 tw-w-11 tw-flex-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-2 tw-border-transparent tw-transition-colors tw-duration-200',
        checked ? 'tw-bg-indigo-600' : 'tw-bg-gray-200',
      ].join(' ')}
      aria-checked={checked}
    >
      <span
        className={[
          'tw-inline-block tw-h-5 tw-w-5 tw-transform tw-rounded-full tw-bg-white tw-shadow tw-ring-0 tw-transition tw-duration-200',
          checked ? 'tw-translate-x-5' : 'tw-translate-x-0',
        ].join(' ')}
      />
    </button>
  </div>
);

// ---- main component ----

const CrewSettingsAccountTab = ({ clientData, onClientUpdated, isLoading }) => {
  const userInfo = useSelector((state) => state.general.user);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isRiverrAdmin = userInfo?.email?.includes('@riverr.ai');

  const formatPayload = (payload) => {
    const clientId = localStorage.getItem('BR_CLIENT_ID');
    return {
      id: clientId,
      name: payload?.name,
      industry: payload?.industry,
      country: payload?.country,
      address: payload?.address,
      identifiers: { regNo: payload?.registrationNo, classNo: payload?.classificationNo },
      support: { email: payload?.supportEmail, phone: payload?.supportPhone },
      contact: {
        name: payload?.contantName,
        website: payload?.website,
        fax: payload?.faxNo,
        phone: payload?.contactPhone,
        email: payload?.contactEmail,
      },
      hasMedEncounter: payload?.hasMedEncounter,
      hasWellnessKPI: payload?.hasWellnessKPI,
      hasMentalPEME: payload?.hasMentalPEME,
      hasPEMEStatus: payload?.hasPEMEStatus,
      logo: payload?.logo,
      language: 'en',
    };
  };

  const editClient = async (payload) => {
    try {
      setIsSaving(true);
      const fPayload = formatPayload(payload);
      await ClientService.updateClientById('', fPayload);
      if (onClientUpdated) await onClientUpdated();
      setIsSaving(false);
      setIsEditing(false);
    } catch (error) {
      setIsSaving(false);
      toast.error(<ErrorToast error={error} />);
    }
  };

  const toggleFeature = async (field) => {
    if (!clientData) return;
    await editClient({ ...clientData, [field]: !Boolean(clientData[field]) });
  };

  if (isLoading || !clientData) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-48">
        <div className="tw-w-8 tw-h-8 tw-border-4 tw-border-indigo-600 tw-border-t-transparent tw-rounded-full tw-animate-spin" />
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="tw-p-6">
        <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-4">Edit Account Details</h3>
        <ClientDetailsForm
          client={clientData}
          onSubmit={editClient}
          onCancelClick={() => setIsEditing(false)}
          isSaving={isSaving}
        />
      </div>
    );
  }

  return (
    <div className="tw-p-6 tw-space-y-6">
      {/* Admin-only toggles */}
      {isRiverrAdmin && (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-5">
          <p className="tw-text-xs tw-uppercase tw-tracking-widest tw-font-semibold tw-text-indigo-500 tw-mb-3">
            Riverr Admins Only
          </p>
          <div className="tw-divide-y tw-divide-gray-100">
            <ToggleSwitch
              checked={Boolean(clientData?.hasMedEncounter)}
              onChange={() => toggleFeature('hasMedEncounter')}
              label="Medical Encounter Insights"
              description="Add medical encounter insights dashboard for this client."
            />
            <ToggleSwitch
              checked={Boolean(clientData?.hasWellnessKPI)}
              onChange={() => toggleFeature('hasWellnessKPI')}
              label="Wellness KPI Insights"
              description="Enable wellness KPI dashboard for this client."
            />
            <ToggleSwitch
              checked={Boolean(clientData?.hasMentalPEME)}
              onChange={() => toggleFeature('hasMentalPEME')}
              label="Mental health PEME"
              description="Enable Mental Health PEME insights for this client."
            />
            <ToggleSwitch
              checked={Boolean(clientData?.hasPEMEStatus)}
              onChange={() => toggleFeature('hasPEMEStatus')}
              label="PEME Status"
              description="Enable PEME Status tracking dashboard for this client."
            />
          </div>
        </div>
      )}

      {/* External member sync — Riverr-admin-only, separate card to keep
          unrelated changes isolated from the feature toggles block above. */}
      {isRiverrAdmin && (
        <ExternalSyncCard
          clientId={localStorage.getItem('BR_CLIENT_ID')}
          userEmail={userInfo?.email}
        />
      )}

      {/* Company info */}
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-5">
        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-4">Account Details</h3>
        <div className="tw-grid tw-grid-cols-2 tw-gap-x-8 tw-gap-y-5">
          <InfoField label="Company Name" value={clientData?.name} />
          <InfoField label="Registration No." value={clientData?.identifiers?.regNo} />
          <InfoField label="Industry" value={clientData?.industry} />
          <InfoField label="Classification No." value={clientData?.identifiers?.classNo} />
          <InfoField label="Address" value={clientData?.address} />
          <InfoField label="Country" value={clientData?.country} />
        </div>
      </div>

      {/* Connected clinics */}
      {clientData?.connectedClinics?.length > 0 && (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-5">
          <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-4">Connected Clinics</h3>
          <div className="tw-space-y-2">
            {clientData.connectedClinics.map((clinic) => (
              <div key={clinic.id} className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-700">
                <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-indigo-400 tw-flex-shrink-0" />
                {clinic.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit button */}
      <div className="tw-flex tw-justify-end">
        <button
          onClick={() => setIsEditing(true)}
          className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-indigo-600 tw-text-white tw-text-sm tw-font-medium tw-rounded-lg tw-border-0 tw-cursor-pointer hover:tw-bg-indigo-700 tw-transition-colors"
        >
          <Edit className="tw-w-4 tw-h-4" />
          Edit
        </button>
      </div>
    </div>
  );
};

export default CrewSettingsAccountTab;
